// Raccourcis de report (WKF-013 §7.4) — retour PO 2026-07-07 : reporter une
// notification/tâche en UN clic (inspiré des menus « Reporter » type Superhuman).
// La mutation snooze accepte déjà { id, snoozed_until } → pas de modale pour les
// cas courants. Chaque preset porte l'ISO cible ET une icône ; le menu affiche
// l'heure résolue à droite (lève l'ambiguïté « ça revient quand ? »).

import {
    CalendarClock,
    CalendarDays,
    CalendarRange,
    Clock,
    Sunrise,
    Sunset,
    type LucideIcon,
} from 'lucide-react'

export interface SnoozePreset {
    key: string
    labelKey: string
    icon: LucideIcon
    iso: string
}

function withTime(base: Date, h: number, m = 0): Date {
    const d = new Date(base)
    d.setHours(h, m, 0, 0)
    return d
}

/** Prochain lundi (jamais aujourd'hui), 9h. */
function nextMonday(from: Date): Date {
    const d = new Date(from)
    const day = d.getDay() // 0 = dimanche
    const delta = (8 - (day === 0 ? 7 : day)) % 7 || 7
    d.setDate(d.getDate() + delta)
    return withTime(d, 9)
}

/**
 * Presets de report d'après l'heure courante. Tous garantis dans le FUTUR
 * (les échéances passées sont omises → cohérent avec le backend
 * `ensure_future_snooze`). Ordre chronologique.
 */
export function snoozePresets(now: Date = new Date()): SnoozePreset[] {
    const hour = now.getHours()
    const out: SnoozePreset[] = []

    // Dans 1 heure (minutes conservées, secondes remises à zéro).
    const in1h = new Date(now.getTime() + 60 * 60 * 1000)
    in1h.setSeconds(0, 0)
    out.push({ key: 'inOneHour', labelKey: 'notifications.snooze.preset.inOneHour', icon: Clock, iso: in1h.toISOString() })

    // Ce soir (18h) — seulement si on n'y est pas encore.
    if (hour < 18) {
        out.push({ key: 'thisEvening', labelKey: 'notifications.snooze.preset.thisEvening', icon: Sunset, iso: withTime(now, 18).toISOString() })
    }

    // Demain matin (9h) + demain soir (18h).
    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)
    out.push({ key: 'tomorrowMorning', labelKey: 'notifications.snooze.preset.tomorrowMorning', icon: Sunrise, iso: withTime(tomorrow, 9).toISOString() })
    out.push({ key: 'tomorrowEvening', labelKey: 'notifications.snooze.preset.tomorrowEvening', icon: Sunset, iso: withTime(tomorrow, 18).toISOString() })

    // Plus tard cette semaine (jeudi 9h) — seulement si on est avant jeudi.
    const day = now.getDay() // 0 dim … 4 jeudi
    if (day >= 1 && day < 4) {
        const thursday = new Date(now)
        thursday.setDate(thursday.getDate() + (4 - day))
        out.push({ key: 'laterThisWeek', labelKey: 'notifications.snooze.preset.laterThisWeek', icon: CalendarClock, iso: withTime(thursday, 9).toISOString() })
    }

    // Semaine prochaine (lundi 9h).
    out.push({ key: 'nextWeek', labelKey: 'notifications.snooze.preset.nextWeek', icon: CalendarDays, iso: nextMonday(now).toISOString() })

    // Dans un mois (même jour, 9h).
    const inMonth = new Date(now)
    inMonth.setMonth(inMonth.getMonth() + 1)
    out.push({ key: 'inOneMonth', labelKey: 'notifications.snooze.preset.inOneMonth', icon: CalendarRange, iso: withTime(inMonth, 9).toISOString() })

    return out
}
